import { useEffect, useState } from "react";
import { t } from "i18next";

import { StatusCode } from "api/protocols";
import { GetCountry } from "api/rpc/2024-04/country";
import { ICountry } from "redux/reducers/models/customer";
import {
  GetTeeOnPaymentAccounts,
  PutActivateAccount,
  PutAcceptTerms,
  IActivateAccountParams,
  PostTeeOnPaymentRepresentative,
  TPostTeeOnPaymentRepresentative,
  GetTeeOnPaymentRepresentative,
  TPutTeeOnPaymentRepresentative,
  PutTeeOnPaymentRepresentative,
} from "api/rpc/2024-04/facilityAdmin/payment/teeonPayments/accounts";
import { GetBankAccount, PostBankAccount } from "api/rpc/2024-04/facilityAdmin/facility/externalAcount";

import { showError, showSuccess } from "redux/actions/ui";
import { TTeeOnPaymentRepresentative } from "redux/reducers/models/transaction";
import { useAppDispatch, useAppSelector } from "hooks/redux";

import {
  IBusinessProfileForm,
  IPayoutAccountForm,
  IProcessingFee,
  IRepresentativeForm,
  TTeeOnActivationStep,
  TTeeOnPaymentsView,
  isTeeOnPaymentsSupported,
} from "./teeOnPaymentsTypes";
import { TBankAccountExternal } from "redux/reducers/models/facility";
import { GetProcessingFees } from "api/rpc/2024-04/facilityAdmin/payment/processingFee";
import { IEditRepresentativeState } from "./components/AccountDashboard";
import { isValidPhone, isValidUrl, COMPANY_NAME_MAX_LENGTH, ADDRESS_MAX_LENGTH } from "./teeOnPaymentsValidation";

const defaultBusinessProfile: IBusinessProfileForm = {
  businessType: null,
  businessStructure: "",
  legalBusinessName: "",
  operatingName: "",
  registrationNumber: "",
  businessAddress: "",
  city: "",
  country: "",
  countryCode: "",
  province: "",
  provinceName: "",
  postalCode: "",
  phone: "",
  website: "",
};

export default function useTeeOnPayments() {
  const dispatch = useAppDispatch();
  const { facilityStore } = useAppSelector(store => store);
  const facilityCountryCode = facilityStore.facility?.country_code?.toUpperCase() ?? "";

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const [view, setView] = useState<TTeeOnPaymentsView>("intro");
  const [step, setStepState] = useState<TTeeOnActivationStep>(1);

  const [businessProfile, setBusinessProfile] = useState<
    IBusinessProfileForm & { activated?: boolean; stripeChargesEnabled?: boolean; stripePayoutsEnabled?: boolean }
  >(defaultBusinessProfile);
  const [payoutAccounts, setPayoutAccounts] = useState<IPayoutAccountForm[]>([]);
  const [representatives, setRepresentatives] = useState<IRepresentativeForm[]>([]);

  const [bankAccounts, setBankAccounts] = useState<TBankAccountExternal[]>([]);
  const [addedRepresentatives, setAddedRepresentatives] = useState<TTeeOnPaymentRepresentative[]>([]);
  const [processingFees, setProcessingFees] = useState<IProcessingFee | undefined>(undefined);
  const [activationCountries, setActivationCountries] = useState<ICountry[] | undefined>(undefined);

  const completedSteps = step === 1 ? [] : step === 2 ? [1] : [1, 2];

  function handleStepChange(nextStep: TTeeOnActivationStep) {
    if (nextStep >= 2 && !isBusinessProfileComplete(businessProfile)) {
      dispatch(showError("Please complete all business profile fields before continuing."));
      return;
    }

    setStepState(nextStep);
  }

  useEffect(() => {
    if (!facilityStore.facility?.id) {
      return;
    }

    void loadAccount();
  }, [facilityStore.facility?.id, facilityStore.facility?.country_code]);

  useEffect(() => {
    if (view !== "activate" || activationCountries !== undefined) {
      return;
    }

    let cancelled = false;

    void (async () => {
      const res = await GetCountry({ provinces: true }, false);
      if (cancelled) {
        return;
      }
      if (res.status !== StatusCode.OK) {
        dispatch(showError(t("Error loading countries.")));
        setActivationCountries([]);
        return;
      }
      setActivationCountries(res.data ?? []);
    })();

    return () => {
      cancelled = true;
    };
  }, [view, activationCountries, dispatch]);

  async function loadAccount() {
    setIsLoading(true);

    if (!isTeeOnPaymentsSupported(facilityCountryCode)) {
      setView("intro");
      setIsLoading(false);
      return;
    }

    const res = await GetTeeOnPaymentAccounts(null, false);

    if (res.status !== StatusCode.OK) {
      dispatch(showError("No Tee On Payments account found, please activate your account."));
      setView("intro");
      setIsLoading(false);
      return;
    }

    const loadedAccount = res.data;

    if(loadedAccount?.stripe_connect_id === null) {
      dispatch(showError("Tee On Payments is not connected to a Stripe account, please connect your account."));
      setView("intro");
      setIsLoading(false);
      return;
    }

    setBusinessProfile(prev => ({
      ...prev,
      activated: loadedAccount?.stripe_connect_account?.requirements?.disabled_reason === null ? true : false,
      legalBusinessName: loadedAccount.company_name ?? "",
      operatingName: loadedAccount.business_profile_name ?? "",
      registrationNumber: loadedAccount.company_registration_number ?? "",
      businessAddress: loadedAccount.company_address_line_1 ?? "",
      city: loadedAccount.company_city ?? "",
      country: loadedAccount.company_country_code ?? loadedAccount.country_code ?? "",
      countryCode: loadedAccount.company_country_code ?? loadedAccount.country_code ?? "",
      province: loadedAccount.company_province_name ?? "",
      provinceName: loadedAccount.company_province_name ?? "",
      postalCode: loadedAccount.company_postal ?? "",
      phone: loadedAccount.company_phone ?? "",
      website: loadedAccount.business_profile_url ?? "",
      stripeChargesEnabled: Boolean(loadedAccount.stripe_charges_enabled),
      stripePayoutsEnabled: Boolean(loadedAccount.stripe_payouts_enabled),
    }));

    const [bankAccountResponse, representativeResponse] = await Promise.all([
      GetBankAccount({}, false),
      GetTeeOnPaymentRepresentative(undefined, false),
    ]);

    setBankAccounts(bankAccountResponse?.status === StatusCode.OK ? bankAccountResponse.data : []);
    setAddedRepresentatives(representativeResponse?.status === StatusCode.OK ? representativeResponse.data : []);

    setView("dashboard");
    setIsLoading(false);
    void loadProcessingFees();
  }

  function startActivation() {
    if (!isTeeOnPaymentsSupported(facilityCountryCode)) {
      dispatch(
        showError(
          t(
            "Tee On Payments is currently not available in your country. Stripe does not fully support Dominican Republic transactions at this time.",
          ),
        ),
      );
      return;
    }

    setStepState(1);
    setView("activate");
  }

  function cancelActivation() {
    setView("intro");
    setStepState(1);
    setActivationCountries(undefined);
  }

  function saveBusinessAndContinue(values: IBusinessProfileForm) {
    if (!isBusinessProfileComplete(values)) {
      dispatch(showError("Please complete all business profile fields before continuing."));
      return;
    }

    setBusinessProfile(values);
    setStepState(2);
  }

  function addPayoutAccount(accountToAdd: IPayoutAccountForm) {
    setPayoutAccounts(prev => {
      if (accountToAdd.isDefault) {
        return [...prev.map(item => ({ ...item, isDefault: false })), accountToAdd];
      }
      return [...prev, accountToAdd];
    });
  }

  function removePayoutAccount(index: number) {
    setPayoutAccounts(prev => {
      const next = prev.filter((_, i) => i !== index);
      const hadDefaultRemoved = prev[index]?.isDefault;
      if (hadDefaultRemoved && next.length > 0 && !next.some(a => a.isDefault)) {
        return next.map((account, i) => (i === 0 ? { ...account, isDefault: true } : account));
      }
      return next;
    });
  }

  function continueFromPayoutConfiguration() {
    setStepState(3);
  }

  function addRepresentative(representativeToAdd: IRepresentativeForm) {
    setRepresentatives(prev => [...prev, representativeToAdd]);
  }

  function removeRepresentative(index: number) {
    setRepresentatives(prev => prev.filter((_, i) => i !== index));
  }

  async function saveRepresentativeChanges(editRepresentativeState: IEditRepresentativeState): Promise<boolean> {
    setIsSaving(true);

    const params: TPutTeeOnPaymentRepresentative = {
      person_id: editRepresentativeState.representativeId,
      first_name: editRepresentativeState.firstName,
      last_name: editRepresentativeState.lastName,
      email: editRepresentativeState.email,
      relationship: {
        director: editRepresentativeState.relationshipType === "director",
        executive: editRepresentativeState.relationshipType === "executive",
        owner: editRepresentativeState.relationshipType === "owner",
        representative: editRepresentativeState.relationshipType === "representative",
      },
    };

    const putRepresentativeResponse = await PutTeeOnPaymentRepresentative(params, true);

    if (putRepresentativeResponse.status !== StatusCode.OK) {
      dispatch(showError("There was an error updating the representative"));
      setIsSaving(false);
      return false;
    }

    dispatch(showSuccess("Representative updated successfully"));

    const refreshed = await GetTeeOnPaymentRepresentative(undefined, false);
    if (refreshed?.status === StatusCode.OK) {
      setAddedRepresentatives(refreshed.data);
    }

    setIsSaving(false);
    return true;
  }

  async function activateTeeOnPayments() {
    const companyAndBusinessParams: IActivateAccountParams = {
      email: "",
      company_name: businessProfile.legalBusinessName,
      company_address_line_1: businessProfile.businessAddress,
      company_address_line_2: "",
      company_city: businessProfile.city,
      company_country_code: businessProfile.countryCode,
      company_province_name: businessProfile.provinceName,
      company_postal: businessProfile.postalCode,
      company_phone: businessProfile.phone,
      company_registration_number: businessProfile.registrationNumber,
      business_profile_name: businessProfile.operatingName || businessProfile.legalBusinessName,
      business_profile_mcc: businessProfile.businessType != null ? String(businessProfile.businessType) : "",
      business_profile_product_description: "",
      business_profile_support_email: "",
      business_profile_support_phone: "",
      business_profile_support_url: "",
      business_profile_url: businessProfile.website ? businessProfile.website.replace(/^https?:\/\//, "") : "",
    };

    const resp = await PutActivateAccount(true, companyAndBusinessParams);

    if (resp.status !== StatusCode.OK) {
      dispatch(showError("There was an error activating Tee On Payments"));
      return false;
    }

    return true;
  }

  async function addPayoutAccounts() {
    const countryCode = facilityCountryCode || businessProfile.countryCode || "";
    const defaultCurrency = facilityStore.facility?.default_currency_code ?? payoutAccounts[0]?.currency ?? "CAD";
    for (const payoutAccount of payoutAccounts) {
      const routingNumber =
        countryCode === "CA"
          ? `${payoutAccount.transitNumber}-${payoutAccount.institutionNumber}`
          : payoutAccount.routingNumber;

      const postBankAccountRequest = {
        type: "bank_account" as const,
        currency: payoutAccount.currency || defaultCurrency,
        country: countryCode,
        currency_default: payoutAccount.isDefault,
        account_number: payoutAccount.accountNumber,
        routing_number: routingNumber,
      };

      const bankAccountResponse = await PostBankAccount(postBankAccountRequest, true);

      if (bankAccountResponse.status !== StatusCode.OK) {
        const msg =
          bankAccountResponse?.data?.message ??
          bankAccountResponse?.message ??
          "There was an error adding the bank account.";
        dispatch(showError(String(msg)));
        return false;
      }
    }
    return true;
  }

  async function addTeeOnRepresentatives() {
    for (const representative of representatives) {
      let dobDate: Date | null = null;

      if (representative.dateOfBirth instanceof Date) {
        dobDate = representative.dateOfBirth;
      } else if (typeof representative.dateOfBirth === "string" && representative.dateOfBirth) {
        dobDate = new Date(representative.dateOfBirth);
      }

      const dobYear = dobDate ? dobDate.getFullYear() : undefined;
      const dobMonth = dobDate ? dobDate.getMonth() + 1 : undefined;
      const dobDay = dobDate ? dobDate.getDate() : undefined;

      const postRepresentativeParams: TPostTeeOnPaymentRepresentative = {
        first_name: representative.firstName,
        last_name: representative.lastName,
        address_line_1: representative.homeAddress,
        city: representative.city,
        postal: representative.postalCode,
        province_id: representative.province ? Number(representative.province) : undefined,
        country_id: representative.country ? Number(representative.country) : undefined,
        dob_day: dobDay,
        dob_month: dobMonth,
        dob_year: dobYear,
        id_number: representative.sin ? representative.sin : representative.ssn,
        phone: representative.phoneNumber,
        email: representative.email,
        relationship: {
          director: representative.relationshipType === "director",
          executive: representative.relationshipType === "executive",
          owner: representative.relationshipType === "owner",
          representative: representative.relationshipType === "representative",
        },
      };

      const representativeResponse = await PostTeeOnPaymentRepresentative(postRepresentativeParams, true);
      if (representativeResponse.status !== StatusCode.OK) {
        dispatch(
          showError(
            "There was an error adding " +
              representative.firstName +
              " " +
              representative.lastName +
              " as a representative",
          ),
        );
        return false;
      }
    }

    return true;
  }

  async function completeActivation() {
    setIsSaving(true);
    try {
      if (!(await activateTeeOnPayments())) {
        return false;
      }
      if (!(await addPayoutAccounts())) {
        return false;
      }
      if (!(await addTeeOnRepresentatives())) {
        return false;
      }
      const termsRes = await PutAcceptTerms(true);
      if (termsRes.status !== StatusCode.OK) {
        dispatch(showError("There was an error accepting Tee On Payments terms"));
        return false;
      }
      dispatch(showSuccess("Successfully activated Tee On Payments"));
      setView("dashboard");
      void loadAccount();
      return true;
    } finally {
      setIsSaving(false);
    }
  }

  async function loadProcessingFees() {
    const res = await GetProcessingFees(null, false);
    if (res.status !== StatusCode.OK) {
      dispatch(showError("Error loading processing fees"));
      return;
    }
    setProcessingFees(res.data as IProcessingFee);
  }

  return {
    isLoading,
    isSaving,
    view,
    step,
    completedSteps,
    businessProfile,
    payoutAccounts,
    bankAccounts,
    addedRepresentatives,
    representatives,
    processingFees,
    activationCountries,
    facilityCountryCode,
    setBusinessProfile,
    startActivation,
    cancelActivation,
    saveBusinessAndContinue,
    addPayoutAccount,
    removePayoutAccount,
    continueFromPayoutConfiguration,
    addRepresentative,
    removeRepresentative,
    saveRepresentativeChanges,
    completeActivation,
    setStep: handleStepChange,
  };
}

export function isBusinessProfileComplete(
  values: Partial<IBusinessProfileForm> & {
    businessType?: number | null;
  },
): boolean {
  const isNonEmptyString = (value: string | undefined): boolean => typeof value === "string" && value.trim().length > 0;
  const isValidNumericString = (value: string | undefined): boolean =>
    isNonEmptyString(value) && Number.isFinite(Number(value));

  return (
    typeof values.businessType === "number" &&
    Number.isFinite(values.businessType) &&
    isNonEmptyString(values.businessStructure) &&
    isNonEmptyString(values.legalBusinessName) &&
    (values.legalBusinessName?.trim().length ?? 0) <= COMPANY_NAME_MAX_LENGTH &&
    isNonEmptyString(values.operatingName) &&
    isNonEmptyString(values.registrationNumber) &&
    isNonEmptyString(values.businessAddress) &&
    (values.businessAddress?.trim().length ?? 0) <= ADDRESS_MAX_LENGTH &&
    isNonEmptyString(values.city) &&
    isValidNumericString(values.country) &&
    isValidNumericString(values.province) &&
    isNonEmptyString(values.postalCode) &&
    isNonEmptyString(values.phone) &&
    isValidPhone(values.phone ?? "") &&
    isNonEmptyString(values.website) &&
    isValidUrl(values.website ?? "")
  );
}
